import { useEffect, useState } from "react";
import { SelectOption } from "types/SelectTypes";
import "./Select.scss";
import Select, { components, ActionMeta, MultiValue } from "react-select";
import { FieldAttributes, useField, useFormikContext } from "formik";
import SVG from "shared/SVG/SVG";
import { SVG_ENUM } from "enums";

interface SelectProps extends FieldAttributes<any> {
  label?: string;
  options?: SelectOption[];
  placeholder?: string;
  name: string;
  filterOption?: any;
  onSelectChange?: (e: any) => void;
  className?: string;
  isSearchable?: boolean;
  optional?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
}

const CustomClearIndicator = (props: any) => {
  return (
    <components.ClearIndicator {...props}>
      <SVG type={SVG_ENUM.CLOSE} />
    </components.ClearIndicator>
  );
};

const CustomDropdownIndicator = (props: any) => {
  const { selectProps } = props;
  return (
    <components.DropdownIndicator {...props}>
      <SVG
        type={
          selectProps.menuIsOpen ? SVG_ENUM.CHEVRON_UP : SVG_ENUM.CHEVRON_DOWN
        }
      />
    </components.DropdownIndicator>
  );
};

const SelectComponent = ({
  label,
  options,
  name,
  filterOption,
  placeholder,
  onSelectChange,
  className = "",
  isSearchable = false,
  isMulti = false,
  optional,
  disabled = false,
  isClearable,
  ...props
}: SelectProps) => {
  const [selectedOption, setSelectedOption] = useState<
    SelectOption | MultiValue<SelectOption> | null
  >(null);
  const [inputValue, setInputValue] = useState("");
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const formik = useFormikContext();
  const [field, meta] = useField({ ...props, name });

  useEffect(() => {
    if (field.value !== undefined) {
      if (isMulti) {
        const initialValue = options?.filter((option) =>
          field.value.includes(option.value),
        );
        setSelectedOption(initialValue || null);
      } else {
        const initialValue = options?.find(
          (option) => option.value === field.value,
        );
        setSelectedOption(initialValue || null);
      }
    } else {
      setSelectedOption(null);
    }
    // eslint-disable-next-line
  }, [field.value, options]);

  const handleChange = (
    newValue: SelectOption | MultiValue<SelectOption> | null,
    actionMeta?: ActionMeta<SelectOption>,
  ) => {
    if (isMulti) {
      if (!newValue) {
        setSelectedOption([]);
        formik.setFieldValue(field.name, []);
        onSelectChange && onSelectChange(null);
        return;
      }
      setSelectedOption(newValue as MultiValue<SelectOption>);
      const valueToStore = (newValue as MultiValue<SelectOption>)?.map(
        (option) => option.value,
      );
      formik.setFieldValue(field.name, valueToStore);
      onSelectChange && onSelectChange(newValue);
    } else {
      if (!newValue) {
        setSelectedOption(null);
        formik.setFieldValue(field.name, "");
        onSelectChange && onSelectChange(null);
        return;
      }
      setSelectedOption(newValue as SelectOption);
      // @ts-ignore
      const valueToStore = newValue ? newValue.value : "";
      formik.setFieldValue(field.name, valueToStore);
      onSelectChange && onSelectChange(newValue);
    }
  };
  return (
    <div className="select__wrapper">
      <div className="select__label">
        {label}{" "}
        {optional && <span className="input__optional">(opcjonalne)</span>}
      </div>
      <Select
        {...props}
        value={selectedOption}
        name={name}
        inputValue={isSearchable ? inputValue : undefined}
        onInputChange={isSearchable ? (val) => setInputValue(val) : undefined}
        onMenuOpen={isSearchable ? () => { setMenuIsOpen(true); setInputValue(""); } : undefined}
        onMenuClose={isSearchable ? () => setMenuIsOpen(false) : undefined}
        controlShouldRenderValue={isSearchable ? !menuIsOpen : true}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          ClearIndicator: CustomClearIndicator,
        }}
        onChange={handleChange}
        placeholder={placeholder ? placeholder : ""}
        options={options}
        className={`select ${className}`}
        classNamePrefix="select"
        filterOption={filterOption}
        isSearchable={isSearchable}
        noOptionsMessage={() => "Brak opcji"}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        isDisabled={disabled}
        isClearable={isClearable === undefined ? optional : isClearable}
      />
      {meta.touched && meta.error && (
        <div className="input__error input__error--select">
          <span>{meta.error}</span>
        </div>
      )}
    </div>
  );
};

export default SelectComponent;
